// Atribuição de marketing — port fiel do captureAttribution/enrichWithAttribution do tracker.ts v1.
// Captura os identificadores de clique e as utm da URL, persiste com janela de 30 dias, e enriquece
// os eventos. Adaptadores injetáveis (params/storage/now) = testável. Agnóstico (REGRA #1).

export const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Os identificadores de CLIQUE que a plataforma captura, e as chaves onde cada um persiste.
 *
 * `gbraid` e `wbraid` entraram em 06/10/2026, e a ausência deles não era cosmética. O Google
 * substitui o `gclid` por um dos dois quando a pessoa chega por um caminho em que o `gclid` não
 * pode existir: `wbraid` na travessia web→app e `gbraid` em campanhas de app, ambos ligados ao
 * modo restrito de consentimento do iOS. Nesses casos **a URL não traz `gclid` nenhum** — então,
 * antes disto, o lead chegava sem identificador de clique e a conversão era inatribuível. Não
 * aparecia como erro em lugar nenhum: aparecia como campanha que não converte.
 *
 * Capturar os três é o que torna a atribuição possível; QUAL deles enviar é decisão de quem
 * consome, e o contrato de conversão offline aceita exatamente um por evento.
 */
const IDENTIFICADORES_DE_CLIQUE = ["fbclid", "gclid", "gbraid", "wbraid"];

const K = {
  fbclid: ["synqi3_fbclid", "synqi3_fbclid_ts"],
  gclid: ["synqi3_gclid", "synqi3_gclid_ts"],
  gbraid: ["synqi3_gbraid", "synqi3_gbraid_ts"],
  wbraid: ["synqi3_wbraid", "synqi3_wbraid_ts"],
  utm: ["synqi3_utm", "synqi3_utm_ts"],
};
const UTM_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "utm_campaign_id", "utm_adset_id", "utm_ad_id",
];

/**
 * Captura a atribuição da URL, persistindo/recuperando dentro da janela.
 * @param {object} o
 * @param {{get:(name:string)=>string|null}} o.params  ex.: new URLSearchParams(location.search)
 * @param {{getItem:(k:string)=>string|null, setItem:(k:string,v:string)=>void}} o.storage
 * @param {()=>number} [o.now]
 * @returns {{fbclid?:string, gclid?:string, gbraid?:string, wbraid?:string, utm?:Record<string,string>}}
 */
export function captureAttribution({ params, storage, now = Date.now }) {
  const out = {};
  const within = (tsKey) => now() - parseInt(storage.getItem(tsKey) || "0", 10) < ATTRIBUTION_WINDOW_MS;

  for (const name of IDENTIFICADORES_DE_CLIQUE) {
    const [key, tsKey] = K[name];
    const v = params.get(name);
    if (v) {
      storage.setItem(key, v);
      storage.setItem(tsKey, String(now()));
      out[name] = v;
    } else {
      const stored = storage.getItem(key);
      if (stored && within(tsKey)) out[name] = stored;
    }
  }

  const [utmKey, utmTsKey] = K.utm;
  const utm = {};
  let hasUtm = false;
  for (const p of UTM_PARAMS) {
    const v = params.get(p);
    if (v) {
      utm[p] = v;
      hasUtm = true;
    }
  }
  if (hasUtm) {
    storage.setItem(utmKey, JSON.stringify(utm));
    storage.setItem(utmTsKey, String(now()));
    out.utm = utm;
  } else {
    const stored = storage.getItem(utmKey);
    if (stored && within(utmTsKey)) {
      try {
        out.utm = JSON.parse(stored);
      } catch {
        /* JSON inválido, ignora */
      }
    }
  }
  return out;
}

/** Enriquece as props do evento com a atribuição capturada (só o que existe). */
export function enrichWithAttribution(props, attribution) {
  if (!attribution) return props;
  const add = {};
  // Só o que EXISTE entra: chave com string vazia viraria um identificador de clique vazio no
  // evento, e quem consome não distinguiria "não houve clique de anúncio" de "houve e eu perdi".
  for (const name of IDENTIFICADORES_DE_CLIQUE) {
    if (attribution[name]) add[name] = attribution[name];
  }
  if (attribution.utm && Object.keys(attribution.utm).length > 0) add.utm = attribution.utm;
  return Object.keys(add).length ? { ...props, ...add } : props;
}
